import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { BundledAddOnItem, CheckoutContext } from "contexts/Checkout";
import { OrganizationUserContext } from "contexts/Organization";
import {
  SplashPage,
  SubscriptionContext,
  SubscriptionTab,
} from "contexts/Subscription";
import { useContext, useEffect } from "react";
import { AddOnItemName } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import { useCountDown } from "features/Home/Banner/useCountdown";
import { accessToPartnershipData } from "utils/influencer";
import {
  formatEpochTimestamp,
  getHoursInFuture,
  isTimeToday,
} from "utils/time";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import { StripeInvoiceLineItem, TabOptions } from "../schema";
import styles from "../styles";
import { useCheckout } from "../useCheckout";
import PartnershipDataBundle from "./CrossSells/PartnershipDataBundle";
import PitchingTemplateBundle from "./CrossSells/PitchingTemplateBundle";
import VaAddOn from "./CrossSells/VaAddOn";
import PromotionalCode from "./PromotionalCode";

interface Props {
  displayName: string | undefined;
  productId: string;
}

const InvoicePreview = ({ displayName, productId }: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { currentOrg } = useContext(OrganizationUserContext);
  const { tab, setTab, subscription, plans } = useContext(SubscriptionContext);
  const { display, createTimer } = useCountDown();
  const {
    addOnItems,
    applyAddOnLoading,
    invoice,
    requiresBillingAddress,
    setPlan,
    plan,
    splashPageSource,
    invoiceLoading,
  } = useContext(CheckoutContext);

  const { applyAddOnItem, removePromoCode, isAddOnItem, updatePreviewInvoice } =
    useCheckout();

  // Variables
  const hasDiscount = invoice?.discounts?.find(
    (x) => x.promotion_code && x.promotion_code?.length > 0,
  );
  const negativeBalance =
    invoice?.starting_balance && invoice?.starting_balance < 0;
  const hasAccessToPartnershipData = accessToPartnershipData(
    currentOrg,
    subscription,
  );
  const displayProBundle =
    (plan === Plans.PRO || plan === Plans.PRO_YEARLY) &&
    splashPageSource !== SplashPage.pro_bundle;

  const isOOOFlow = splashPageSource === SplashPage.ooo;
  const isProBundleFlow = splashPageSource === SplashPage.pro_bundle;
  const isVaFlow = splashPageSource === SplashPage.va_money_back;

  const displayVaBundle =
    (plan === Plans.VA || plan === Plans.VA_YEARLY) &&
    splashPageSource !== SplashPage.va_bundled;

  const canRemoveCoupon =
    !splashPageSource ||
    ![
      SplashPage.ooo,
      SplashPage.pro_bundle,
      SplashPage.va_money_back,
    ]?.includes(splashPageSource);

  const switchTab = async (newTab: SubscriptionTab) => {
    if (tab === newTab) return;
    setTab(newTab);

    let alternativePlan =
      plan && plan in TabOptions
        ? TabOptions[plan as keyof typeof TabOptions]
        : null;

    const emailPlans = [
      AddOnItemName.email_volume_monthly,
      AddOnItemName.email_volume_yearly,
    ];
    const isAddOnEmail = emailPlans.includes(plan as AddOnItemName);

    if (isAddOnEmail) {
      const copy = makeDeepCopy(addOnItems || []);
      const index = copy.findIndex((x: BundledAddOnItem) =>
        emailPlans.includes(x.name as AddOnItemName),
      );
      const item = {
        name: alternativePlan as AddOnItemName,
        quantity: index !== -1 ? copy[index].quantity : 1,
      };
      if (index !== -1) {
        // If quantity is 0, remove the item from the list
        copy.splice(index, 1);
      }
      copy.push(item);
      setPlan(alternativePlan as Plans | AddOnItemName);
      await updatePreviewInvoice({
        plan: alternativePlan as Plans | AddOnItemName,
        addOnItems: copy,
      });
    } else if (alternativePlan) {
      await updatePreviewInvoice({
        plan: alternativePlan,
      });
      setPlan(alternativePlan);
    }
  };

  const getItemName = (row: StripeInvoiceLineItem) => {
    return row?.metadata?.item_name;
  };

  const renderDescription = (description: string) => {
    if (plans && plan && plans[plan as Plans]?.price_annotation) {
      return `${description} ${plans[plan as Plans]?.price_annotation}`;
    }
    if (description.startsWith("1 × ")) {
      return description.slice(4);
    }
    return description;
  };

  const renderTime = (start: number, end: number) => {
    return (
      <Box sx={{ fontSize: 10, color: grey[500] }}>
        {` ${formatEpochTimestamp({
          epochTimestamp: start,
          addYear: true,
        })} - ${formatEpochTimestamp({
          epochTimestamp: end,
          addYear: true,
        })}`}
      </Box>
    );
  };

  const renderBundle = () => {
    if (displayVaBundle) {
      return (
        <Box>
          {[
            tab === SubscriptionTab.yearly
              ? AddOnItemName.email_volume_yearly
              : AddOnItemName.email_volume_monthly,
          ].map((addOnItemName) => (
            <Box key={addOnItemName} sx={{ mt: 2 }}>
              <VaAddOn addOnItemName={addOnItemName} />
            </Box>
          ))}
        </Box>
      );
    }

    if (displayProBundle) {
      return (
        <>
          {!hasAccessToPartnershipData && <PartnershipDataBundle />}
          <PitchingTemplateBundle />
        </>
      );
    }
    return <></>;
  };

  useEffect(() => {
    const isYearlyPlan =
      plan &&
      [Plans.VA_YEARLY, Plans.PRO_YEARLY, Plans.CONCIERGE_YEARLY]?.includes(
        plan as Plans,
      );
    if (isYearlyPlan && tab !== SubscriptionTab.yearly) {
      setTab(SubscriptionTab.yearly);
    }
    if (
      plan === Plans.VA_YEARLY &&
      splashPageSource === SplashPage.va_money_back
    ) {
      const countdownDate = getHoursInFuture(10);
      createTimer(countdownDate?.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, splashPageSource]);

  const getNextPayDate = () => {
    const nextPaymentDate =
      invoice?.next_payment_attempt || invoice?.period_start;
    if (!nextPaymentDate) return "";
    const isTimeStampToday = isTimeToday(nextPaymentDate);
    if (isTimeStampToday) {
      return <>Total due today</>;
    } else {
      return `Total due on ${formatEpochTimestamp({
        epochTimestamp: nextPaymentDate,
        addYear: true,
      })}`;
    }
  };

  const hideTab =
    splashPageSource === SplashPage.va_bundled
      ? false
      : isOOOFlow ||
        isProBundleFlow ||
        isVaFlow ||
        !(plan && plan in TabOptions);

  const discountAmount =
    Number(invoice?.total_discount_amounts?.[0]?.amount || 0) / 100;
  const totalDue = Number((invoice?.amount_due || 0) / 100);
  const originalAmount = (discountAmount || 0) + totalDue;

  return (
    <Box>
      {!hideTab && (
        <Grid container>
          <Button
            sx={[
              styles.tab,
              tab === SubscriptionTab.monthly ? styles.active : {},
            ]}
            onClick={() => switchTab(SubscriptionTab.monthly)}
          >
            Monthly
          </Button>
          <Button
            sx={[
              styles.tab,
              tab === SubscriptionTab.yearly ? styles.active : {},
            ]}
            onClick={() => switchTab(SubscriptionTab.yearly)}
          >
            Yearly{" "}
            <Box sx={styles.annotation}>
              ✨ 25% {isAddOnItem ? "off" : "+ Free Template"}
            </Box>
          </Button>
        </Grid>
      )}

      <Table size="small" sx={{ mt: 2 }}>
        <TableBody>
          {invoice?.lines?.data?.map((row, index) => (
            <TableRow key={index}>
              <TableCell component="th" scope="row">
                {renderDescription(row.description)}
                {plan === AddOnItemName.custom_email && index === 1 && (
                  <ul
                    style={{
                      color: grey[500],
                      margin: 0,
                      marginTop: "4px",
                      paddingLeft: "20px",
                    }}
                  >
                    <li>Gmail inbox with your domain</li>
                    <li>Monthly hosting</li>
                    <li>Deliverability setup (SPF, DKIM, DMARC)</li>
                  </ul>
                )}

                {displayName &&
                  row?.period?.start &&
                  row?.period?.end &&
                  row?.description?.includes(displayName) &&
                  renderTime(row?.period?.start, row?.period?.end)}

                {displayName &&
                  row?.metadata?.start &&
                  row?.metadata?.end &&
                  renderTime(
                    Number(row?.metadata?.start),
                    Number(row?.metadata?.end),
                  )}

                {plan !== AddOnItemName.email_volume_monthly &&
                  plan !== AddOnItemName.email_volume_yearly &&
                  plan !== AddOnItemName.pitching_templates &&
                  (splashPageSource === SplashPage.va_bundled ||
                    (!isProBundleFlow &&
                      getItemName(row) &&
                      getItemName(row) !== AddOnItemName.oui_studio)) && (
                    <Grid item xs={12}>
                      <LoadingButton
                        size="small"
                        loading={applyAddOnLoading === getItemName(row)}
                        sx={{ fontSize: 10, color: "purple.main", ml: -0.5 }}
                        onClick={async () => {
                          await applyAddOnItem({
                            name: getItemName(row) as AddOnItemName,
                            quantity: 0,
                          });
                        }}
                      >
                        Remove add on
                      </LoadingButton>
                    </Grid>
                  )}
              </TableCell>
              <TableCell align="right">
                ${Number(row.amount / 100).toFixed(2)}
              </TableCell>
            </TableRow>
          ))}

          {displayVaBundle && (
            <TableRow>
              <TableCell component="th" scope="row">
                300+ automated pitches/month
              </TableCell>

              <TableCell align="right">$0.00</TableCell>
            </TableRow>
          )}

          {isOOOFlow && currentOrg?.isVaUser && (
            <TableRow>
              <TableCell component="th" scope="row">
                You already subscribed to Virtual Assistant
              </TableCell>

              <TableCell align="right">$0.00</TableCell>
            </TableRow>
          )}

          {(plan === Plans.PRO_YEARLY || plan === Plans.VA_YEARLY) && (
            <TableRow>
              <TableCell component="th" scope="row">
                1 × Free Template Coupon 🔖
              </TableCell>

              <TableCell align="right">$0.00</TableCell>
            </TableRow>
          )}

          <TableRow>
            <TableCell component="th" scope="row">
              Tax{" "}
              {requiresBillingAddress
                ? "(Enter Address To Calculate)"
                : "(Based On Your Location)"}
            </TableCell>
            <TableCell align="right">
              {invoice?.total_taxes?.[0]?.amount ? (
                <>
                  {" "}
                  ${Number(invoice?.total_taxes?.[0]?.amount / 100).toFixed(2)}
                </>
              ) : (
                "$0.00"
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {hasDiscount || negativeBalance ? (
        <Grid item xs={12} sx={{ mt: 2, px: 2, fontSize: 12 }}>
          Adjustments
        </Grid>
      ) : (
        <></>
      )}

      {invoice?.total_discount_amounts?.map((row, index) => (
        <Grid
          item
          xs={12}
          key={index}
          sx={{ px: 2, py: 1, fontSize: 12 }}
          container
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            disableElevation
            size="small"
            variant="contained"
            sx={styles.coupon}
            onClick={() => {
              if (canRemoveCoupon) {
                removePromoCode();
              }
            }}
          >
            {invoice?.discounts?.[index]?.coupon?.name}

            {canRemoveCoupon && invoice?.discounts?.[index]?.promotion_code && (
              <Box
                component="i"
                className="fa-regular fa-xmark"
                sx={{ ml: 1 }}
              />
            )}
          </Button>

          <Box sx={{ color: grey[500] }}>
            -${Number(row.amount / 100).toFixed(2)}
          </Box>
        </Grid>
      ))}

      {invoice && invoice?.starting_balance !== 0 && (
        <Grid
          item
          xs={12}
          sx={{ px: 2, py: 1, fontSize: 12 }}
          container
          justifyContent="space-between"
        >
          <Box>Credit Balance </Box>
          <Box sx={{ color: grey[500] }}>
            -${Number(Math.abs(invoice?.starting_balance / 100)).toFixed(2)}
          </Box>
        </Grid>
      )}

      <Table
        sx={{
          "&:last-child td, &:last-child th": { border: 0 },
        }}
      >
        <TableBody
          sx={{
            "&:last-child tr, &:last-child th": { border: 0 },
          }}
        >
          <TableRow>
            {(invoice?.next_payment_attempt || invoice?.period_start) && (
              <TableCell component="th" scope="row">
                {getNextPayDate()}
              </TableCell>
            )}
            <TableCell
              component="th"
              scope="row"
              align="right"
              sx={{ fontSize: 18, color: "green.main" }}
            >
              {invoiceLoading ? (
                <Grid container justifyContent={"flex-end"}>
                  <Skeleton animation="wave" width={150} height={30} />
                </Grid>
              ) : (
                <>
                  <Typography sx={{ fontSize: 18, color: "green.main" }}>
                    <Box component="span" sx={{ fontSize: 12 }}>
                      $
                    </Box>
                    {totalDue.toFixed(2)}{" "}
                    <Box component="span" sx={{ textTransform: "uppercase" }}>
                      {invoice?.currency}
                    </Box>
                    {originalAmount && originalAmount > totalDue ? (
                      <Box
                        component="span"
                        sx={{
                          ml: 1,
                          color: "green.main",
                          opacity: 0.2,
                          textDecoration: "line-through",
                        }}
                      >
                        {originalAmount.toFixed(2)}
                      </Box>
                    ) : (
                      <></>
                    )}
                  </Typography>
                  {display && splashPageSource === SplashPage.va_money_back && (
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: "info.main",
                        textTransform: "none",
                      }}
                    >
                      ⏳ Additional 10% off for the next{" "}
                      <strong>{display}</strong>
                    </Typography>
                  )}
                </>
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {tab === SubscriptionTab.monthly &&
        invoice &&
        !invoiceLoading &&
        !isMobileScreen && (
          <Button
            fullWidth
            disableElevation
            variant="contained"
            sx={{
              backgroundColor: "purple.main",
              color: "black",
              borderRadius: 3,
              mb: 2,
              "&:hover": { backgroundColor: "purple.light" },
              fontSize: { xs: 10, md: 13 },
            }}
            onClick={() => {
              trackEvent(
                "Check Out Page - Get a Free Pitch Template with Annual Plan",
              );
              switchTab(SubscriptionTab.yearly);
            }}
          >
            + Get a Free Pitch Template with Annual Plan{" "}
            <Box
              component="i"
              className="fa-regular fa-arrow-right"
              sx={{ ml: 1 }}
            />
          </Button>
        )}

      {!hasDiscount && (
        <Grid container justifyContent="flex-end">
          <PromotionalCode productId={productId} />
        </Grid>
      )}
      {!isMobileScreen && <>{renderBundle()}</>}
    </Box>
  );
};

export default InvoicePreview;
